import { useFormContext, useWatch } from 'react-hook-form';
import * as z from 'zod';

import type { ContactAddressDTO } from '@invenco/common-interface/shared';
import type { CustomerDTO } from '@invenco/sales-interface/interfaces';

import {
  ContactAddressFormField,
  contactAddressFormFieldDefaultValues,
  contactAddressFormFieldSchema,
} from '@/components/forms/address-form-field';
import { CheckboxFormField } from '@/components/forms/checkbox-form-field';
import { TextFormField } from '@/components/forms/text-form-field';

const optionalCustomerAddressSchema = contactAddressFormFieldSchema.extend({
  address1: z.string().trim(),
  country: z.string().trim(),
});

function withCustomerContact(
  address: z.output<typeof optionalCustomerAddressSchema>,
  customer: { customerName: string; email: string; phone: string },
) {
  return {
    ...address,
    contactName: address.contactName || customer.customerName,
    contactEmail: address.contactEmail || customer.email,
    contactNumber: address.contactNumber || customer.phone,
  };
}

export const customerFieldsSchema = z
  .object({
    customerId: z.string().optional(),
    customerName: z.string().trim().min(1, 'Enter a customer name'),
    email: z.string().trim().email('Enter a valid email'),
    phone: z.string().trim().min(1, 'Enter a phone number'),
    shippingAddress: contactAddressFormFieldSchema,
    billingAddressMatchesShippingAddress: z.boolean(),
    billingAddress: optionalCustomerAddressSchema,
  })
  .superRefine(({ billingAddressMatchesShippingAddress, billingAddress }, context) => {
    if (billingAddressMatchesShippingAddress) return;

    if (!billingAddress.address1.trim()) {
      context.addIssue({
        code: 'custom',
        path: ['billingAddress', 'address1'],
        message: 'Enter an address',
      });
    }

    if (!billingAddress.country.trim()) {
      context.addIssue({
        code: 'custom',
        path: ['billingAddress', 'country'],
        message: 'Enter a country',
      });
    }
  })
  .transform(
    ({ billingAddressMatchesShippingAddress, shippingAddress, billingAddress, ...values }) => {
      const shippingAddressWithContact = withCustomerContact(shippingAddress, values);

      return {
        ...values,
        shippingAddress: shippingAddressWithContact,
        billingAddress: billingAddressMatchesShippingAddress
          ? shippingAddressWithContact
          : withCustomerContact(billingAddress, values),
      };
    },
  );

export type CustomerFieldsValues = z.input<typeof customerFieldsSchema>;
export type CustomerFieldsSubmit = z.output<typeof customerFieldsSchema>;

type AddressContactField = 'contactName' | 'contactEmail' | 'contactNumber';
type AddressContactPath = `${'shippingAddress' | 'billingAddress'}.${AddressContactField}`;

export const customerFieldsDefaultValues = {
  customerId: undefined,
  customerName: '',
  email: '',
  phone: '',
  shippingAddress: { ...contactAddressFormFieldDefaultValues },
  billingAddressMatchesShippingAddress: true,
  billingAddress: { ...contactAddressFormFieldDefaultValues },
} satisfies CustomerFieldsValues;

export function getAddressValues(address?: Partial<ContactAddressDTO> | null) {
  return {
    companyName: address?.companyName ?? '',
    contactName: address?.contactName ?? '',
    contactEmail: address?.contactEmail ?? '',
    contactNumber: address?.contactNumber ?? '',
    address1: address?.address1 ?? '',
    address2: address?.address2 ?? '',
    city: address?.city ?? '',
    state: address?.state ?? '',
    postcode: address?.postcode ?? '',
    country: address?.country ?? '',
  };
}

export function addressesMatch(
  first?: Partial<ContactAddressDTO> | null,
  second?: Partial<ContactAddressDTO> | null,
) {
  const firstAddress = getAddressValues(first);
  const secondAddress = getAddressValues(second);
  const addressKeys = Object.keys(firstAddress) as (keyof typeof firstAddress)[];

  return addressKeys.every((addressKey) => {
    return (
      firstAddress[addressKey].trim().toLocaleLowerCase() ===
      secondAddress[addressKey].trim().toLocaleLowerCase()
    );
  });
}

export function getCustomerFieldsValues(customer: CustomerDTO): CustomerFieldsValues {
  return {
    customerId: customer.id,
    customerName: customer.name ?? '',
    email: customer.email ?? '',
    phone: customer.phone ?? '',
    shippingAddress: getAddressValues(customer.shippingAddress),
    billingAddressMatchesShippingAddress: addressesMatch(
      customer.shippingAddress,
      customer.billingAddress,
    ),
    billingAddress: getAddressValues(customer.billingAddress),
  };
}

export function CustomerFields() {
  const form = useFormContext<CustomerFieldsValues>();
  const billingAddressMatchesShippingAddress = useWatch<
    CustomerFieldsValues,
    'billingAddressMatchesShippingAddress'
  >({
    name: 'billingAddressMatchesShippingAddress',
  });
  const syncAddressContactField = (
    fieldName: AddressContactField,
    previousValue: string,
    nextValue: string,
  ) => {
    for (const addressName of ['shippingAddress', 'billingAddress'] as const) {
      const path = `${addressName}.${fieldName}` as AddressContactPath;
      const currentValue = form.getValues(path);

      if (!currentValue.trim() || currentValue === previousValue) {
        form.setValue(path, nextValue, { shouldDirty: true, shouldValidate: true });
      }
    }
  };

  return (
    <div className="grid gap-4">
      <TextFormField
        name="customerName"
        label="Name"
        aria-label="Name"
        onChange={(event, previousValue) => {
          syncAddressContactField('contactName', previousValue, event.currentTarget.value);
        }}
      />
      <div className="grid gap-4 sm:grid-cols-2">
        <TextFormField
          name="email"
          label="Email"
          aria-label="Email"
          type="email"
          onChange={(event, previousValue) => {
            syncAddressContactField('contactEmail', previousValue, event.currentTarget.value);
          }}
        />
        <TextFormField
          name="phone"
          label="Phone"
          aria-label="Phone"
          onChange={(event, previousValue) => {
            syncAddressContactField('contactNumber', previousValue, event.currentTarget.value);
          }}
        />
      </div>
      <ContactAddressFormField name="shippingAddress" label="Shipping address" />
      <CheckboxFormField
        name="billingAddressMatchesShippingAddress"
        label="Billing address matches shipping address"
        aria-label="Billing address matches shipping address"
      />
      {!billingAddressMatchesShippingAddress && (
        <ContactAddressFormField name="billingAddress" label="Billing address" />
      )}
    </div>
  );
}
